/**
 * Live WhatsApp-style preview of a single Flow screen — renders the visual
 * definition (headings, paragraphs, inputs, dropdowns, etc.) the way it will look
 * inside a WhatsApp Flow, so non-technical users can build by sight (no JSON).
 */

import { Stack, Text, Group, Badge } from '../../../../components/ui';
import type { FlowScreen, FlowField } from './flowDefinition';

function FauxLabel({ field }: { field: FlowField }) {
  return (
    <Group gap={4} mb={4}>
      <Text size="xs" fw={500}>{field.label || field.name || 'Question'}</Text>
      {field.required && <Text size="xs" c="red">*</Text>}
    </Group>
  );
}

const boxStyle: React.CSSProperties = {
  border: '1px solid var(--mantine-color-default-border)',
  borderRadius: 8,
  background: 'var(--mantine-color-body)',
  padding: '8px 10px',
  fontSize: 12,
  color: 'var(--mantine-color-dimmed)',
};

function Field({ field }: { field: FlowField }) {
  switch (field.type) {
    case 'TextHeading':
      return <Text fw={700} size="md">{field.text || 'Heading'}</Text>;
    case 'TextSubheading':
      return <Text fw={600} size="sm">{field.text || 'Subheading'}</Text>;
    case 'TextBody':
      return <Text size="sm" c="dimmed">{field.text || 'Paragraph text'}</Text>;
    case 'TextInput':
      return <div><FauxLabel field={field} /><div style={boxStyle}>Short answer…</div></div>;
    case 'TextArea':
      return <div><FauxLabel field={field} /><div style={{ ...boxStyle, minHeight: 48 }}>Long answer…</div></div>;
    case 'DatePicker':
      return <div><FauxLabel field={field} /><div style={boxStyle}>📅 Select a date</div></div>;
    case 'Dropdown':
      return <div><FauxLabel field={field} /><div style={boxStyle}>{(field.options.split(',')[0]?.trim()) || 'Choose…'} ▾</div></div>;
    case 'RadioButtonsGroup':
      return (
        <div>
          <FauxLabel field={field} />
          <Stack gap={4}>
            {(field.options.split(',').map((o) => o.trim()).filter(Boolean).slice(0, 6).length
              ? field.options.split(',').map((o) => o.trim()).filter(Boolean).slice(0, 6)
              : ['Option 1', 'Option 2']).map((o, i) => (
              <Group key={i} gap={6} wrap="nowrap">
                <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--mantine-color-gray-5)' }} />
                <Text size="xs">{o}</Text>
              </Group>
            ))}
          </Stack>
        </div>
      );
    case 'OptIn':
      return (
        <Group gap={6} wrap="nowrap" align="flex-start">
          <div style={{ width: 14, height: 14, borderRadius: 3, border: '2px solid var(--mantine-color-gray-5)', flexShrink: 0, marginTop: 2 }} />
          <Text size="xs">{field.label || field.name || 'I agree'}</Text>
        </Group>
      );
    default:
      return null;
  }
}

export function FlowScreenPreview({ screen, flowName, screenIndex, screenTotal }: {
  screen: FlowScreen;
  flowName?: string;
  screenIndex?: number;
  screenTotal?: number;
}) {
  return (
    <div
      style={{
        width: 300,
        maxWidth: '100%',
        margin: '0 auto',
        border: '10px solid var(--mantine-color-dark-4)',
        borderRadius: 28,
        overflow: 'hidden',
        background: 'var(--mantine-color-default)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
      }}
    >
      {/* WhatsApp-style header */}
      <div style={{ background: '#075E54', color: 'white', padding: '10px 12px' }}>
        <Text size="xs" style={{ color: 'rgba(255,255,255,0.8)' }}>{flowName || 'WhatsApp Flow'}</Text>
        <Text size="sm" fw={600} style={{ color: 'white' }}>{screen.title || 'Screen'}</Text>
      </div>

      {/* Screen body */}
      <div style={{ padding: 14, minHeight: 320, background: 'var(--mantine-color-body)' }}>
        <Stack gap="sm">
          {screen.fields.length === 0 ? (
            <Text size="xs" c="dimmed" ta="center" mt="lg">Add text or questions to see them here.</Text>
          ) : (
            screen.fields.map((f) => <Field key={f.id} field={f} />)
          )}
        </Stack>
      </div>

      {/* Footer button */}
      <div style={{ padding: 12, background: 'var(--mantine-color-body)' }}>
        <div style={{ background: '#25D366', color: 'white', textAlign: 'center', padding: '10px', borderRadius: 22, fontWeight: 600, fontSize: 13 }}>
          {screen.footerLabel || 'Continue'}
        </div>
        {typeof screenIndex === 'number' && typeof screenTotal === 'number' && (
          <Group justify="center" mt={8}>
            <Badge size="xs" variant="light">Screen {screenIndex + 1} of {screenTotal}{screen.action === 'complete' ? ' · submits' : ''}</Badge>
          </Group>
        )}
      </div>
    </div>
  );
}
